import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { Control, FieldPath, FieldValues } from "react-hook-form";

import { FormControl, FormDescription, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

/**
 * Field primitives every form is built from.
 *
 * `SelectField` deliberately lives in its own module (`select-field.tsx`) rather
 * than here: it is the only field that needs the Radix select primitive, and
 * that primitive drags @radix-ui/react-select plus @floating-ui into whatever
 * imports it. The login form imports `TextField` from this file, so keeping
 * `SelectField` here put a ~135 kB dropdown implementation on the critical path
 * of a screen with two text inputs and a button.
 *
 * Label, control, description and error message always appear in the same order
 * with the same spacing, so a form assembled in ten minutes still looks like the
 * rest of the app. Features never re-wire `FormItem` by hand.
 */

export interface BaseFieldProps<TValues extends FieldValues> {
  control: Control<TValues>;
  name: FieldPath<TValues>;
  label: string;
  description?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

export function FieldLabel({ label, required }: { label: string; required?: boolean }) {
  return (
    <FormLabel>
      {label}
      {required ? (
        <span className="text-destructive" aria-hidden="true">
          *
        </span>
      ) : null}
    </FormLabel>
  );
}

/**
 * Validation messages are i18n KEYS in the zod schemas (contract §7.8); they are
 * resolved here, at render. `t()` returns the input unchanged when it is not a
 * known key, so a message that comes straight from the server still reads fine.
 */
export function FieldMessage({ message }: { message?: string }) {
  const { t } = useTranslation();
  if (!message) return null;
  return (
    <p role="alert" className="text-destructive text-sm">
      {t(message)}
    </p>
  );
}

interface TextFieldProps<TValues extends FieldValues> extends BaseFieldProps<TValues> {
  placeholder?: string;
  type?: "text" | "email" | "password" | "number" | "tel" | "url";
  inputMode?: "text" | "email" | "numeric" | "decimal" | "tel" | "url";
  autoComplete?: string;
  maxLength?: number;
}

export function TextField<TValues extends FieldValues>({
  control,
  name,
  label,
  description,
  required,
  disabled,
  className,
  placeholder,
  type = "text",
  inputMode,
  autoComplete,
  maxLength,
}: TextFieldProps<TValues>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className={className}>
          <FieldLabel label={label} required={required} />
          <FormControl>
            <Input
              {...field}
              value={field.value ?? ""}
              type={type}
              inputMode={inputMode}
              placeholder={placeholder}
              autoComplete={autoComplete}
              maxLength={maxLength}
              disabled={disabled ?? field.disabled}
            />
          </FormControl>
          {description ? <FormDescription>{description}</FormDescription> : null}
          <FieldMessage message={fieldState.error?.message} />
        </FormItem>
      )}
    />
  );
}

interface TextareaFieldProps<TValues extends FieldValues> extends BaseFieldProps<TValues> {
  placeholder?: string;
  rows?: number;
}

export function TextareaField<TValues extends FieldValues>({
  control,
  name,
  label,
  description,
  required,
  disabled,
  className,
  placeholder,
  rows = 3,
}: TextareaFieldProps<TValues>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className={className}>
          <FieldLabel label={label} required={required} />
          <FormControl>
            <Textarea
              {...field}
              value={field.value ?? ""}
              rows={rows}
              placeholder={placeholder}
              disabled={disabled ?? field.disabled}
            />
          </FormControl>
          {description ? <FormDescription>{description}</FormDescription> : null}
          <FieldMessage message={fieldState.error?.message} />
        </FormItem>
      )}
    />
  );
}

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export function SwitchField<TValues extends FieldValues>({
  control,
  name,
  label,
  description,
  disabled,
  className,
}: BaseFieldProps<TValues>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem
          className={cn(
            "flex flex-row items-start justify-between gap-4 rounded-lg border p-3",
            className,
          )}
        >
          <div className="space-y-0.5">
            <FormLabel>{label}</FormLabel>
            {description ? <FormDescription>{description}</FormDescription> : null}
          </div>
          <FormControl>
            <Switch
              checked={Boolean(field.value)}
              onCheckedChange={field.onChange}
              disabled={disabled ?? field.disabled}
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
}

/** Two-column grid that collapses to one column on a phone. */
export function FormGrid({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("grid gap-4 sm:grid-cols-2", className)}>{children}</div>;
}

/** Non-field (root) error banner for a form. */
export function FormRootError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p
      role="alert"
      className="border-destructive/30 bg-destructive/5 text-destructive rounded-md border px-3 py-2 text-sm"
    >
      {message}
    </p>
  );
}
